/** Локальный вердикт на один заход в звонок; unknown не считается нулевой оценкой. */
export type VpnAssessment =
  | { status: 'assessed'; score: number }
  | { status: 'unknown'; score: null }

/**
 * Сужает недоверенный ответ; неверные и неполные данные не включают VPN-подсказку.
 * @param value — JSON от серверного адаптера или сервиса.
 */
export function parseVpnAssessment(value: unknown): VpnAssessment {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const result = value as Record<string, unknown>

    if (
      result.status === 'assessed' &&
      typeof result.score === 'number' &&
      Number.isInteger(result.score) &&
      result.score >= 0 &&
      result.score <= 4
    ) {
      return { status: 'assessed', score: result.score }
    }
  }

  return { status: 'unknown', score: null }
}

/**
 * Загружает оценку через same-origin API без передачи сервисного токена или IP из браузера.
 * @param roomName — комната текущего захода.
 * @param accessToken — JWT участника для авторизации адаптера.
 * @param signal — отмена при выходе из звонка.
 */
export async function requestVpnAssessment(
  roomName: string,
  accessToken: string,
  signal: AbortSignal,
): Promise<VpnAssessment> {
  try {
    let timeZone: string | undefined

    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch {
      // Недоступный Intl не должен отключать независимую сетевую проверку.
    }

    const response = await fetch('/api/vpn_assessment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ roomName, ...(timeZone && { timeZone }) }),
      cache: 'no-store',
      signal,
    })

    return response.ok
      ? parseVpnAssessment(await response.json())
      : { status: 'unknown', score: null }
  } catch {
    return { status: 'unknown', score: null }
  }
}
